// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * The one-time disclosure of an activation code (**ADR-0076** §3).
 *
 * An activation code is a bearer credential. Platform stores only its digest,
 * so the plaintext exists in exactly one response body and nowhere else — there
 * is no route that can answer it again. Two consequences shape this component
 * completely:
 *
 * - **the code lives in React state and nothing else.** It is not written to
 *   `localStorage`, `sessionStorage`, IndexedDB, a cookie, the URL, the hash,
 *   a query parameter, the history entry, an analytics call, or the console.
 *   The transport already forbids caching it (`Cache-Control: no-store` is set
 *   at the BFF), and a browser-side copy would defeat that at the last step; and
 * - **closing is destructive, by construction.** The parent holds the
 *   disclosure in state and unmounts this component to close it, so the
 *   plaintext leaves the component tree and the DOM together. There is no
 *   `keepMounted`, no hidden copy, and nothing to reveal a second time — a
 *   refresh shows the masked last four and only that.
 *
 * The copy control is the reason the disclosure is usable at all, and its
 * outcome is announced rather than merely coloured: a clipboard write can fail
 * for reasons the operator cannot see (a permission policy, a headless
 * context), and an operator who believes a failed copy succeeded closes the one
 * dialog that could have shown them the code.
 *
 * Focus enters this dialog when it opens and the parent returns it to the
 * control that opened it, so an operator using a keyboard or a screen reader is
 * told what happened and put back where they were (`WCAG 2.2 AA`,
 * `docs/09-UX.md`).
 */

import type { ActivationIssuance } from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { formatInstant } from './presentation';

/** Everything a disclosure needs to be shown once and then discarded. */
export interface ActivationDisclosure {
  /** The college the code belongs to, for the dialog's accessible name. */
  readonly collegeLabel: string;
  /** The issuance, including the plaintext code. */
  readonly issuance: ActivationIssuance;
  /** Whether an earlier code was revoked by this issuance. */
  readonly previousCodeRevoked: boolean;
}

/** Props for {@link ActivationDisclosureDialog}. */
export interface ActivationDisclosureDialogProps {
  /** The disclosure to show. The parent renders this component only when it has one. */
  readonly disclosure: ActivationDisclosure;
  /** Close and discard the plaintext. */
  readonly onClose: () => void;
}

/** Outcome of the most recent copy attempt. */
type CopyOutcome = 'untried' | 'copied' | 'failed';

const TITLE_ID = 'activation-disclosure-title';
const DESCRIPTION_ID = 'activation-disclosure-description';

/** Show an activation code exactly once. */
export function ActivationDisclosureDialog({
  disclosure,
  onClose,
}: ActivationDisclosureDialogProps): ReactNode {
  const [copyOutcome, setCopyOutcome] = useState<CopyOutcome>('untried');
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Focus is moved deliberately rather than left to whichever control the
  // library focuses first. The dialog's own body is the target so the warning
  // and the code are what a screen reader reads, not the Copy button in
  // isolation.
  useEffect(() => {
    bodyRef.current?.focus();
  }, []);

  const copy = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(disclosure.issuance.code);
      setCopyOutcome('copied');
    } catch {
      // The failure is not inspected and not logged. Its message would name the
      // API that refused, and the console is a place a code must never reach —
      // the only useful thing to say is that the operator must copy it by hand.
      setCopyOutcome('failed');
    }
  }, [disclosure.issuance.code]);

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      aria-describedby={DESCRIPTION_ID}
      maxWidth="sm"
      fullWidth
      // Restoration is the parent's job: it knows which control opened the
      // dialog and it survives the refetch that follows. Two components
      // competing for focus is how focus ends up on the body.
      disableRestoreFocus
    >
      <DialogTitle id={TITLE_ID}>Activation code for {disclosure.collegeLabel}</DialogTitle>
      <DialogContent>
        <Box ref={bodyRef} tabIndex={-1} sx={{ outline: 'none' }}>
          <Alert severity="warning" id={DESCRIPTION_ID} sx={{ mb: 2 }}>
            Copy this activation code now. It will not be shown again. Give it to the college; after
            you close this dialog, only the last four characters remain visible.
          </Alert>

          <Typography variant="body2" color="text.secondary" component="p">
            Activation code
          </Typography>
          <Typography
            component="code"
            data-testid="activation-code"
            sx={{
              display: 'block',
              fontFamily: 'monospace',
              fontSize: '1.125rem',
              wordBreak: 'break-all',
              p: 1.5,
              mb: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {disclosure.issuance.code}
          </Typography>

          <Typography variant="body2" component="p">
            Expires: {formatInstant(disclosure.issuance.expires_at, 'Not reported')}
          </Typography>
          <Typography variant="body2" component="p">
            Last four characters, which stay visible in the list: {disclosure.issuance.last_four}
          </Typography>
          {disclosure.previousCodeRevoked ? (
            <Typography variant="body2" component="p" sx={{ mt: 1 }}>
              The college&rsquo;s previous activation code has been revoked and will no longer be
              accepted.
            </Typography>
          ) : null}

          {copyOutcome === 'copied' ? (
            <Alert severity="success" role="status" sx={{ mt: 2 }} data-testid="copy-succeeded">
              The activation code was copied to the clipboard.
            </Alert>
          ) : null}
          {copyOutcome === 'failed' ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }} data-testid="copy-failed">
              The activation code could not be copied. Select the code above and copy it manually
              before closing this dialog.
            </Alert>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button
            onClick={() => {
              void copy();
            }}
          >
            Copy code
          </Button>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
